import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Action, Button, TextField, MiniAlert, FlexCol } from '@veeqo/ui';
import { useForm, Controller } from 'react-hook-form';
import { verifyAuthenticatorCode } from 'utils/api';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from 'context/Context';
import { useTrackForm } from 'utils/adobeAnalytics/hooks/useTrackForm';
import { SESSION_EXPIRED_MESSAGE } from 'utils/constants';
import { Form } from '../styled';

type FormData = {
    code: string;
};

type Response = {
    error?: boolean;
    sessionExpired?: boolean;
    redirectTo?: string;
};

export const AuthenticatorVerifyForm = () => {
    const { state } = useAppContext();
    const navigate = useNavigate();
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>();

    const [hasVerificationFailed, setHasVerificationFailed] = useState(false);
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const { trackFormUpdated } = useTrackForm('signin-otp-form');

    const onSubmit = handleSubmit(async (data) => {
        setIsButtonLoading(true);
        setHasVerificationFailed(false);
        const returnTo = state.signupData.return_to;
        const response: Response | undefined = await verifyAuthenticatorCode({
            ...data,
            return_to: returnTo,
        });

        // The verification session has expired: the user can't retry here. Lock the form (disable
        // every action except "Back to login") and show a persistent error, rather than silently
        // redirecting them.
        if (response?.sessionExpired) {
            setIsButtonLoading(false);
            setIsSessionExpired(true);
            return;
        }

        // New flow: any other verification failure keeps the user on this screen with an inline
        // error, rather than bouncing them back to the login form.
        if (response?.error) {
            setIsButtonLoading(false);
            setHasVerificationFailed(true);
            return;
        }

        if (response?.redirectTo) {
            global.location.assign(decodeURIComponent(response.redirectTo));
        } else {
            setIsButtonLoading(false);
            Sentry.captureMessage('No redirect_to when verifying code');
        }
    });

    return (
        <Form onSubmit={onSubmit} id="signin-otp-form">
            <FlexCol alignItems="stretch" gap="xl">
                <FlexCol>
                    <Controller
                        name="code"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <TextField
                                onChange={onChange}
                                onBlur={trackFormUpdated('code')}
                                value={value}
                                aria-label="code"
                                type="text"
                                label="Authenticator code"
                                placeholder="Enter 6-digit code"
                                autoComplete="one-time-code"
                                error={errors.code && errors.code.message}
                            />
                        )}
                        rules={{ required: 'Required' }}
                    />
                    {isSessionExpired ? (
                        <MiniAlert variant="error" title={SESSION_EXPIRED_MESSAGE} />
                    ) : (
                        hasVerificationFailed && (
                            <MiniAlert
                                variant="error"
                                title="The code you entered was incorrect. Please try again."
                            />
                        )
                    )}
                </FlexCol>
                <FlexCol>
                    <Button
                        type="submit"
                        variant="primary"
                        loading={isButtonLoading}
                        disabled={isButtonLoading || isSessionExpired}
                    >
                        Verify
                    </Button>
                    <Action
                        variant="flat"
                        disabled={isSessionExpired}
                        onClick={() => navigate('backup')}
                    >
                        Use a backup code
                    </Action>
                </FlexCol>
            </FlexCol>
        </Form>
    );
};
